import { useState } from 'react';
import { CUSTOMER_SITE_DETAIL_ACTIONS } from './customerSiteDetailActions';
import {
  exportCustomerSiteDetailExcel,
  exportCustomerSiteDetailPDF,
  formatSiteDetailCellValue,
} from './customerSiteDetailExport';
import { displayError } from '../../helpers';

const DETAIL_CONFIG = {
  quotation: {
    title: 'Quotation Details',
    columns: [
      { key: 'document_no', label: 'Quotation No' },
      { key: 'document_date', label: 'Date' },
      { key: 'grade', label: 'Grade' },
      { key: 'rate', label: 'Rate (Incl. Tax)', align: 'end' },
      { key: 'quantity', label: 'Qty', align: 'center' },
      { key: 'amount', label: 'Amount', align: 'end' },
      { key: 'status', label: 'Status', align: 'center' },
    ],
  },
  order: {
    title: 'Sales Order Details',
    columns: [
      { key: 'document_no', label: 'Order No' },
      { key: 'document_date', label: 'Date' },
      { key: 'grade', label: 'Grade' },
      { key: 'quantity', label: 'Qty', align: 'center' },
      { key: 'amount', label: 'Amount', align: 'end' },
      { key: 'status', label: 'Status', align: 'center' },
    ],
  },
  schedule: {
    title: 'Work Schedule Details',
    columns: [
      { key: 'document_no', label: 'Schedule No' },
      { key: 'document_date', label: 'Schedule Date' },
      { key: 'delivery_date', label: 'Delivery Date' },
      { key: 'grade', label: 'Grade' },
      { key: 'quantity', label: 'Qty', align: 'center' },
      { key: 'status', label: 'Status', align: 'center' },
    ],
  },
  dc: {
    title: 'List of Delivery Challans',
    columns: [
      { key: 'document_no', label: 'DC No' },
      { key: 'document_date', label: 'DC Date' },
      { key: 'dc_time', label: 'DC Time', align: 'center' },
      { key: 'so_no', label: 'SO No.' },
      { key: 'schedule_no', label: 'Schedule No' },
      { key: 'grade', label: 'Grade' },
      { key: 'quantity', label: 'Qty', align: 'center' },
      { key: 'vehicle_no', label: 'Vehicle No.' },
      { key: 'invoice_no', label: 'Invoice No.' },
      { key: 'rate', label: 'Rate (Incl. Tax)', align: 'end' },
      { key: 'amount', label: 'Amount', align: 'end' },
    ],
  },
};

function CustomerSiteStatsDetailModal({
  open,
  title,
  detailType,
  customerName,
  siteLabel,
  rows,
  loading,
  printingRowId,
  cookies,
  onClose,
  onPrintRow,
  onEditRow,
}) {
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  if (!open) {
    return null;
  }

  const config = DETAIL_CONFIG[detailType] || { title: title || 'Details', columns: [] };
  const actionConfig = CUSTOMER_SITE_DETAIL_ACTIONS[detailType];
  const reportTitle = title || config.title;
  const canExport = !loading && rows.length > 0;

  const handleExcelExport = async () => {
    setExportingExcel(true);
    try {
      await exportCustomerSiteDetailExcel({
        cookies,
        columns: config.columns,
        rows,
        title: reportTitle,
        detailType,
        customerName,
        siteLabel,
      });
    } catch (error) {
      displayError(error?.response?.data ?? error, 'Excel export failed');
    } finally {
      setExportingExcel(false);
    }
  };

  const handlePdfExport = async () => {
    setExportingPdf(true);
    try {
      await exportCustomerSiteDetailPDF({
        cookies,
        columns: config.columns,
        rows,
        title: reportTitle,
        detailType,
        customerName,
        siteLabel,
      });
    } catch (error) {
      displayError(error?.response?.data ?? error, 'PDF export failed');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="modal customer-site-stats-modal" tabIndex="-1" style={{ display: 'block' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header customer-site-stats-modal-header">
            <h5 className="modal-title">{reportTitle}</h5>
            <div className="customer-site-stats-modal-header-actions">
              {canExport && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-light customer-site-stats-export-btn"
                    title="Download Excel"
                    aria-label="Download Excel"
                    disabled={exportingExcel}
                    onClick={handleExcelExport}
                  >
                    <i className="fa fa-file-excel-o text-success" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light customer-site-stats-export-btn"
                    title="Download PDF"
                    aria-label="Download PDF"
                    disabled={exportingPdf}
                    onClick={handlePdfExport}
                  >
                    <i className="fa fa-file-pdf-o text-danger" aria-hidden="true" />
                  </button>
                </>
              )}
              <button type="button" className="close text-white" aria-label="Close" onClick={onClose}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <div className="modal-body">
            {(customerName || siteLabel) && (
              <div className="customer-site-stats-modal-meta">
                {customerName && (
                  <div className="customer-site-stats-modal-meta-row">
                    <span className="customer-site-stats-modal-meta-label">Customer Name:</span>
                    <span>{customerName}</span>
                  </div>
                )}
                {siteLabel && (
                  <div className="customer-site-stats-modal-meta-row">
                    <span className="customer-site-stats-modal-meta-label">Site:</span>
                    <span>{siteLabel}</span>
                  </div>
                )}
              </div>
            )}
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-4">No records found.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered customer-site-stats-detail-table">
                  <thead>
                    <tr>
                      {config.columns.map((column) => (
                        <th
                          key={column.key}
                          className={column.align ? `text-${column.align}` : undefined}
                        >
                          {column.label}
                        </th>
                      ))}
                      {actionConfig && <th className="text-center">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={row.id || index}>
                        {config.columns.map((column) => (
                          <td
                            key={column.key}
                            className={column.align ? `text-${column.align}` : undefined}
                          >
                            {formatSiteDetailCellValue(row[column.key], column.key)}
                          </td>
                        ))}
                        {actionConfig && (
                          <td className="text-center customer-site-stats-row-actions">
                            {actionConfig.print && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary me-1"
                                disabled={printingRowId === row.id}
                                onClick={() => onPrintRow?.(row)}
                              >
                                {printingRowId === row.id ? 'Printing...' : 'Print'}
                              </button>
                            )}
                            {actionConfig.editPath && (
                              <button
                                type="button"
                                className="btn btn-sm btn-info text-white"
                                onClick={() => onEditRow?.(row)}
                              >
                                Edit
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerSiteStatsDetailModal;
